<script>
  import { format } from 'svelte-i18n';
  import { Badge, Tooltip } from '@sveltestrap/sveltestrap';

  import { Porthole } from '@client/components/porthole';
  import { ShowMore } from '@client/components/show-more';
  import { Spinner } from '@client/components/spinner';

  import { ReportsService } from '@client/services/reports';

  import { appStore } from '@client/stores/app';
  import { personas } from '@client/stores/domain-settings';
  import { recipientsStore } from '@client/stores/recipients';

  import { nameFromEmail } from '@client/utils/users';

  import { PERSONA_DEFAULTS } from '@client/enums';

  import { capitalize, generateUUID } from '@common/utils/strings';

  import { lazyload } from '@client/actions/lazy-load';

  export let activity = {};
  export let email = '';
  export let showCompanyLogo = false;
  export let showExpanded = false;
  export let trackingDisabled = false;

  let photoError = false;

  const fetchRecipientReport = async (email) => {
    const cachedRecipient = $recipientsStore[email];

    if (!cachedRecipient) {
      $recipientsStore[email] = {};
      const response = await ReportsService.getRecipientReport({ email });
      $recipientsStore[email] = response;

      return $recipientsStore[email];
    }

    return cachedRecipient;
  };

  const updateSrc = function () {
    this.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  };

  const id = generateUUID();

  // const launchSalesforceContact = async (email) => {
  //   const response = await UsersService.getSalesforceContact({ email });
  // };

  const personasConfig = {
    roles: $personas?.roles || PERSONA_DEFAULTS.roles,
    departments: $personas?.departments || PERSONA_DEFAULTS.departments,
    executives: $personas?.executives || PERSONA_DEFAULTS.executives,
    influencers: $personas?.influencers || PERSONA_DEFAULTS.influencers,
    excluded: $personas?.excluded || PERSONA_DEFAULTS.excluded
  };

  const INCLUDED_BUYER_ROLES = new RegExp(personasConfig.roles.join('|'), 'gi');
  const INCLUDED_BUYER_DEPARTMENTS = new RegExp(personasConfig.departments.join('|'), 'gi');
  const INCLUDED_EXECUTIVE_TITLES = new RegExp(personasConfig.executives.join('|'), 'gi');
  const INCLUDED_INFLUENCER_TITLES = new RegExp(personasConfig.influencers.join('|'), 'gi');
  const EXCLUDED_KEYWORDS = new RegExp(personasConfig.excluded.join('|'), 'gi');

  const isBuyerPersona = (person) => {
    const title = person?.employment?.title?.toLowerCase();

    if (!title) {
      return false;
    }

    INCLUDED_BUYER_ROLES.lastIndex = 0;
    INCLUDED_EXECUTIVE_TITLES.lastIndex = 0;
    INCLUDED_BUYER_DEPARTMENTS.lastIndex = 0;
    INCLUDED_INFLUENCER_TITLES.lastIndex = 0;
    EXCLUDED_KEYWORDS.lastIndex = 0;

    const hasRole = INCLUDED_BUYER_ROLES.test(title);
    const hasDepartment = INCLUDED_BUYER_DEPARTMENTS.test(title);

    return ((hasRole && hasDepartment) || INCLUDED_EXECUTIVE_TITLES.test(title)) && !EXCLUDED_KEYWORDS.test(title);
  };

  const isInfluencerPersona = (person) => {
    const title = person?.employment?.title?.toLowerCase();

    if (!title) {
      return false;
    }

    INCLUDED_BUYER_ROLES.lastIndex = 0;
    INCLUDED_EXECUTIVE_TITLES.lastIndex = 0;
    INCLUDED_BUYER_DEPARTMENTS.lastIndex = 0;
    INCLUDED_INFLUENCER_TITLES.lastIndex = 0;
    EXCLUDED_KEYWORDS.lastIndex = 0;

    return INCLUDED_INFLUENCER_TITLES.test(title) && !EXCLUDED_KEYWORDS.test(title);
  };

  $: stats = activity.reduce(
    (acc, item) => {
      if (email !== item.recipient) {
        return acc;
      }

      if (item.objecttype === 'beaconview') {
        acc.emailviews.count++;
      } else if (item.objecttype === 'linkclick') {
        acc.linkclicked.count++;
      } else if (item.objecttype === 'websitevisit') {
        acc.websitevisited.count++;
      } else if (item.objecttype === 'adclick') {
        acc.adclicked.count++;
      } else if (item.verb === 'myplayerlaunched' || item.verb === 'playerlaunched') {
        acc.documentlaunched.count++;
      } else if (item.verb === 'myviewerlaunched' || item.verb === 'viewerlaunched') {
        acc.documentlaunched.count++;
      } else if (item.verb === 'downloaded') {
        acc.documentdownloaded.count++;
      }

      return acc;
    },
    {
      emailviews: {
        tooltip: $format('label.EMAIL_VIEWS'),
        icon: 'os-icon-eye',
        count: 0
      },
      linkclicked: {
        tooltip: $format('label.LINK_CLICKS'),
        icon: 'os-icon-link',
        count: 0
      },
      documentdownloaded: {
        tooltip: $format('label.DOWNLOADS'),
        icon: 'os-icon-download',
        count: 0
      },
      documentlaunched: {
        icon: 'os-icon-external-link',
        tooltip: $format('label.DOCUMENT_OPENED'),
        count: 0
      },
      websitevisited: {
        icon: 'os-icon-globe',
        tooltip: $format('label.WEBSITE_VISITS'),
        count: 0
      },
      adclicked: {
        icon: 'os-icon-target',
        tooltip: $format('label.AD_CLICKS'),
        count: 0
      }
    }
  );
</script>

<div class="person" use:lazyload={{ rootMargin: '300px 0px', renderVisible: $appStore.mobile }}>
  {#await fetchRecipientReport(email)}
    <div class="loader">
      <Spinner show />
    </div>
  {:then recipient}
    {@const person = recipient?.person || {}}
    {@const company = recipient?.company || {}}

    {#if !showCompanyLogo && $recipientsStore[email]?.email && !photoError}
      <div class="break">
        <span>{$format('label.RECIPIENT')}</span>
      </div>
    {/if}
    <div class="images">
      {#key $recipientsStore[email]}
        <Porthole
          details={{
            photoUrl: person?.avatar || person?.gravatar?.avatar || '',
            name: person?.name?.fullName || email,
            email
          }}
        />
      {/key}

      {#if showCompanyLogo && $recipientsStore[email]?.email && !photoError}
        <div class="company-logo">
          <img
            src="https://logo.clearbit.com/{email.split('@').pop()}"
            on:error={() => (photoError = true)}
            on:load={updateSrc}
            loading="lazy"
            referrerpolicy="no-referrer"
            alt={email}
            style="background: url(https://logo.clearbit.com/{email
              .split('@')
              .pop()}) no-repeat center center / contain; height: 20px; width: 20px; overflow: hidden;"
          />
        </div>
      {/if}
    </div>

    {#if $recipientsStore[email]?.person}
      <div class="important-badges">
        {#if isBuyerPersona(person)}
          <div class="buyer">{$format('label.BUYER')}</div>
        {:else if isInfluencerPersona(person)}
          <div class="influencer">{$format('label.INFLUENCER')}</div>
        {/if}
      </div>
    {/if}

    <div>
      {#if person?.name?.fullName}
        <div><b>{person?.name.fullName}</b></div>
      {:else}
        <div><b>{nameFromEmail(email)}</b></div>
      {/if}

      {#if person?.employment?.title}
        <div class="title">{person?.employment.title}</div>
      {/if}

      <div class="email">{email}</div>

      <div class="socials">
        <!-- {#if $userStore.hasSalesforce}
          <div on:click={() => launchSalesforceContact($recipientsStore[email].email)} role="button" tabindex="0">
            <i id="user-salesforce-{id}" class="os-icon-salesforce" />

            <Tooltip target="user-salesforce-{id}">{$format('label.VIEW_SALESFORCE_CONTACT')}</Tooltip>
          </div>
        {/if} -->
        {#if person?.linkedin?.handle}
          <a href={person?.linkedin.handle} target="_blank">
            <i class="os-icon-linkedin-filled" />
          </a>
        {/if}
        {#if person?.twitter?.handle}
          <a href={person?.twitter.handle} target="_blank">
            <i class="os-icon-twitter-filled" />
          </a>
        {/if}
        {#if person?.github?.handle}
          <a href={person?.github.handle} target="_blank">
            <i class="os-icon-github-filled" />
          </a>
        {/if}
        {#if person?.facebook?.handle}
          <a href={person?.facebook.handle} target="_blank">
            <i class="os-icon-facebook-filled" />
          </a>
        {/if}
      </div>

      <div class="stats">
        {#each Object.entries(stats) as [value, config]}
          {#if config.count}
            <div id="user-stats-tooltip-{value}-{id}">
              <i class={config.icon} />
              <span>{config.count}</span>
            </div>
            <Tooltip target="user-stats-tooltip-{value}-{id}">
              {config.tooltip}
            </Tooltip>
          {/if}
        {/each}
      </div>

      {#if person?.employment?.title}
        <ShowMore type="small" bind:expanded={showExpanded}>
          <div class="meta">
            {#if person?.employment.name}
              <div>
                <b>{$format('label.COMPANY')}</b>
                <div>{person?.employment.name}</div>
              </div>
            {/if}

            {#if company?.category?.industry}
              <div class="capitalize">
                <b>{$format('label.INDUSTRY')}</b>
                <div>{company?.category?.industry}</div>
              </div>
            {/if}

            {#if person?.employment.role}
              <div class="capitalize">
                <b>{$format('label.DEPARTMENT')}</b>
                <div>{capitalize(person?.employment.role).replace(/_/g, ' ')}</div>
              </div>
            {/if}
            {#if person?.employment.seniority}
              <div class="capitalize">
                <b>{$format('label.LEVEL')}</b>
                <div>{capitalize(person?.employment.seniority)}</div>
              </div>
            {/if}
            {#if person?.location}
              <div>
                <b>{$format('label.LOCATION')}</b>
                <div>{person?.location.replace(/null,/g, '')}</div>
              </div>
            {/if}
          </div>
        </ShowMore>
      {/if}

      {#if trackingDisabled}
        <Badge color="danger" class="badge-danger mt-2">
          {$format('label.TRACKING_DISABLED')}
        </Badge>
      {/if}
    </div>
  {/await}
</div>

<style lang="scss">
  .person {
    align-items: start;
    display: grid;
    gap: 16px;
    grid-template-columns: 60px auto;
    position: relative;

    .loader {
      align-items: center;
      display: flex;
      gap: 16px;
      justify-content: center;
      margin: var(--margin-large) auto;
      min-width: 100%;
      text-align: center;
    }

    .important-badges {
      display: flex;
      flex-direction: column;
      gap: 8px;
      position: absolute;
      right: -4px;
      top: -4px;

      > div {
        border: 1px solid var(--color-blue-400);
        border-radius: var(--border-radius-small);
        color: var(--color-blue-400);
        font-size: 8px;
        padding: calc(var(--padding-small) / 2) var(--padding-small);
        text-transform: uppercase;

        &.influencer {
          border: 1px solid var(--color-green-500);
          color: var(--color-green-500);
        }
      }
    }

    .images {
      display: flex;
      flex-direction: column;
      gap: 12px;
      align-items: center;
      justify-content: center;
    }

    .company-logo {
      background-color: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: 100%;
      margin-top: -22px;
      padding: 2px;

      img {
        border-radius: 100%;
      }
    }

    .meta {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: var(--margin-base);
    }

    .break {
      word-break: break-all;
    }

    .email {
      color: var(--color-gray-700);
      word-break: break-all;
    }

    .socials {
      display: flex;
      gap: 8px;

      a,
      div {
        cursor: pointer;
        display: inline-block;
        margin-top: var(--margin-base);

        i {
          color: var(--color-gray-700);

          &:hover {
            color: var(--color-gray-900);
          }
        }
      }
    }

    .stats {
      align-items: center;
      display: flex;
      font-size: var(--font-size-medium);
      gap: 8px;

      > div {
        margin-top: var(--margin-base);
      }
    }
  }
</style>
